import { createSignal, createEffect, onCleanup, Show } from 'solid-js';
import { useApi, isOk } from '../api';
import { ProfileInfoSnapshot } from '../models/ProfileInfo';
import { Citybuild } from '../models/Citybuild';
import { formatTime as fmtTime } from '../services/dateFormat';
import Text from './Text';
import styles from './ProfileInfoCard.module.css';

interface ProfileInfoCardProps {
    botId: string;
        pollMs?: number;
}

function formatCurrency(n: number): string {
    return new Intl.NumberFormat('de-DE').format(n) + '$';
}

function formatTime(iso: string): string {
    return fmtTime(iso, iso, true);
}

export default function ProfileInfoCard(props: ProfileInfoCardProps) {
    const api = useApi();
    const [snap, setSnap] = createSignal<ProfileInfoSnapshot | null>(null);
    let timer: number | undefined;

    const fetchLatest = async () => {
        if (!props.botId) return;
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

        const res = await api.profileInfo.getLatest(props.botId);
        if (isOk(res.data)) setSnap((res.data.value as unknown as ProfileInfoSnapshot) ?? null);
    };

    createEffect(() => {
        const id = props.botId;
        if (timer !== undefined) window.clearInterval(timer);
        setSnap(null);
        if (!id) return;
        fetchLatest();
        timer = window.setInterval(fetchLatest, props.pollMs ?? 30_000);
    });

    onCleanup(() => {
        if (timer !== undefined) window.clearInterval(timer);
    });

    return (
        <div class={styles.card}>
            <div class={styles.header}>
                <h2 class={styles.title}>
                    <i class="fa-solid fa-id-card" />
                    <Text val="PROFILE_INFO_TITLE" />
                    <Show when={snap()}>
                        <span class={styles.timestamp}>{formatTime(snap()!.createdAt)}</span>
                    </Show>
                </h2>
                <a class={styles.historyLink} href={`/bots/${props.botId}/profile-history`}>
                    <Text val="PROFILE_INFO_HISTORY" /> <i class="fa-solid fa-arrow-right" />
                </a>
            </div>

            <Show
                when={snap()}
                fallback={<div class={styles.empty}><Text val="PROFILE_INFO_EMPTY" /></div>}
            >
                <div class={styles.grid}>
                    <div class={styles.stat}>
                        <div class={styles.statLabel}><Text val="STAT_CASH" /></div>
                        <div class={styles.statValue}>{formatCurrency(snap()!.cash)}</div>
                    </div>
                    <div class={styles.stat}>
                        <div class={styles.statLabel}><Text val="STAT_BANK" /></div>
                        <div class={styles.statValue}>{formatCurrency(snap()!.bankBalance)}</div>
                    </div>
                    <div class={styles.stat}>
                        <div class={styles.statLabel}><Text val="STAT_ORBS" /></div>
                        <div class={styles.statValue}><Show when={snap()!.orbs != null} fallback={<Text val="ORBS_UNKNOWN" />}>{snap()!.orbs!.toLocaleString('de-DE')}</Show></div>
                    </div>
                    <div class={styles.stat}>
                        <div class={styles.statLabel}><Text val="STAT_KRISTALLE" /></div>
                        <div class={styles.statValue}><Show when={snap()!.kristalle != null} fallback={<Text val="KRISTALLE_UNKNOWN" />}>{snap()!.kristalle!.toLocaleString('de-DE')}</Show></div>
                    </div>
                    <div class={styles.stat}>
                        <div class={styles.statLabel}><Text val="STAT_HOURS" /></div>
                        <div class={styles.statValue}>{snap()!.hoursPlayed.toFixed(1)}</div>
                    </div>
                    <div class={styles.stat}>
                        <div class={styles.statLabel}><Text val="CITYBUILD" /></div>
                        <div class={styles.statValue}>{Citybuild[snap()!.citybuild] ?? snap()!.citybuild}</div>
                    </div>
                    <div class={styles.stat}>
                        <div class={styles.statLabel}><Text val="STAT_ONLINE" /></div>
                        <div class={styles.statValue}>{snap()!.currentPlayers}/{snap()!.maxPlayers}</div>
                    </div>
                </div>
            </Show>
        </div>
    );
}
